import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const DEFAULT_PROGRAMS = [2, 9]; // Campus Leaders Program dan Bumdes (The New CLP)

/**
 * A reusable Batch Selection component that groups batches by main program.
 *
 * @param {Object} props
 * @param {string|number} props.value - Current selected batch ID
 * @param {function} props.onChange - Callback when batch selection changes (receives batchId)
 * @param {Array<Object>} [props.options] - Optional array of batch options to use instead of fetching.
 * @param {string|number|Array} [props.programId] - Program ID to fetch batches for (default: [2, 9])
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Additional inline styles
 * @param {string} [props.placeholder] - Placeholder text for the selection
 */
const BatchSelector = ({
  value,
  onChange,
  options = null,
  programId = DEFAULT_PROGRAMS, // CLP dan BumDesa
  className = '',
  style = {},
  placeholder = 'Pilih Batch',
}) => {
  const { authUser } = useAuth();
  const role = authUser?.activeSession?.roleCode?.toLowerCase();

  const [internalBatchOptions, setInternalBatchOptions] = useState([]);
  const [loading, setLoading] = useState(!options);

  useEffect(() => {
    if (options) {
      setLoading(false);
      return;
    }

    const fetchBatches = async () => {
      setLoading(true);
      try {
        let data;
        if (role === 'mentor') {
          data = await api.getBatchOptionsMentors({ programId });
        } else {
          data = await api.getBatchOptions({ programId });
        }
        setInternalBatchOptions(data);
      } catch (error) {
        console.error('Failed to fetch batch options:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBatches();
  }, [options, programId, role]);

  const rawOptions = options || internalBatchOptions;

  // Transform new API structure to react-select grouped options format
  const groupedOptions = useMemo(() => {
    if (!rawOptions || !Array.isArray(rawOptions)) return [];

    return rawOptions.map((mainGroup) => ({
      label: `${mainGroup.mainProgramName} - Batch ${mainGroup.mainBatchName}`,
      options: (mainGroup.subBatches || []).map((sub) => ({
        value: sub.batchId,
        label: `${sub.programName} - Batch ${sub.batchName}`,
        statusOffered: sub.statusOfred ?? sub.statusOffered,
      })),
    }));
  }, [rawOptions]);

  // Flatten options for finding the current value object
  const flatOptions = useMemo(() => {
    return groupedOptions.reduce(
      (acc, group) => [...acc, ...group.options],
      [],
    );
  }, [groupedOptions]);

  const selectedOption =
    flatOptions.find((opt) => String(opt.value) === String(value)) || null;

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      fontSize: '0.875rem',
      borderRadius: '0.5rem',
      border: '1px solid #dee2e6',
      boxShadow: state.isFocused
        ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
        : 'none',
      borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
      '&:hover': {
        borderColor: '#86b7fe',
      },
      ...style,
    }),
    groupHeading: (provided) => ({
      ...provided,
      color: '#495057',
      fontWeight: '700',
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      padding: '8px 12px',
      backgroundColor: '#f8f9fa',
      borderBottom: '1px solid #eee',
      marginBottom: '4px',
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '0.875rem',
      padding: '8px 16px',
      backgroundColor: state.isSelected
        ? '#0d6efd'
        : state.isFocused
          ? '#e9ecef'
          : 'transparent',
      color: state.isSelected ? '#fff' : '#212529',
      cursor: 'pointer',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      overflow: 'hidden',
      boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
      zIndex: 1050,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const formatOptionLabel = ({ label, statusOffered }) => (
    <div className='d-flex justify-content-between align-items-center w-100 gap-3'>
      <span className='text-truncate'>{label}</span>
      <span
        className={`badge rounded-pill ${statusOffered ? 'bg-success' : 'bg-secondary'} flex-shrink-0`}
        style={{ fontSize: '0.65rem' }}
      >
        {statusOffered ? 'Open' : 'Closed'}
      </span>
    </div>
  );

  return (
    <div
      className={`batch-selector-container ${className}`}
      style={{ minWidth: '220px' }}
    >
      <Select
        value={selectedOption}
        onChange={(opt) => onChange(opt ? opt.value : '')}
        options={groupedOptions}
        isLoading={loading}
        placeholder={placeholder}
        styles={customStyles}
        formatOptionLabel={formatOptionLabel}
        isClearable={false}
        classNamePrefix='batch-select'
        menuPortalTarget={document.body}
      />
    </div>
  );
};

export default BatchSelector;
